<template>
    <div class="page-container">
        <!-- 头部导航 -->
        <header class="header" :class="{ 'header-fixed': isScrolled }">
            <div class="header-content">
                <!-- Logo -->
                <div class="logo" @click="goHome">
                    <img src="@/assets/logo.png" alt="互联造物" class="logo-img">
                </div>
                
                <!-- 导航菜单 -->
                <nav class="nav-menu hidden-xs">
                    <a 
                        v-for="(item, index) in navList" 
                        :key="index"
                        :class="{ active: currentNav === index }"
                        @click="handleNav(index)"
                        class="nav-item"
                    >
                        {{ item }}
                    </a>
                </nav>
                
                <!-- 移动端菜单按钮 -->
                <div class="menu-toggle hidden-sm hidden-md hidden-lg" @click="toggleMobileMenu">
                    <Icon :type="mobileMenuOpen ? 'ios-close' : 'ios-menu'" size="28" />
                </div>
            </div>
            
            <!-- 移动端菜单 -->
            <div v-show="mobileMenuOpen" class="mobile-menu">
                <a 
                    v-for="(item, index) in navList" 
                    :key="index"
                    :class="{ active: currentNav === index }"
                    @click="handleMobileNav(index)"
                    class="mobile-nav-item"
                >
                    {{ item }}
                </a>
            </div>
        </header>

        <!-- 轮播图 -->
        <section class="banner-section">
            <Carousel 
                radius-dot 
                dots="inside" 
                :autoplay="bannerSettings.autoplay"
                :autoplay-speed="bannerSettings.autoplaySpeed"
                :loop="bannerSettings.loop"
                @on-change="handleBannerChange"
            >
                <CarouselItem v-for="(banner, index) in bannerList" :key="index">
                    <div class="banner-item" :style="{ backgroundImage: `url(${banner.image})` }">
                        <div class="banner-content">
                            <h2 class="banner-title">{{ banner.title }}</h2>
                            <p class="banner-desc">{{ banner.description }}</p>
                            <Button type="primary" size="large" class="banner-btn">
                                {{ banner.buttonText }}
                            </Button>
                        </div>
                    </div>
                </CarouselItem>
            </Carousel>
        </section>

        <!-- 公司简介 -->
        <section class="section about-section">
            <div class="container">
                <div class="section-title">
                    <h2>关于我们</h2>
                    <p>ABOUT US</p>
                </div>
                <Row :gutter="60" type="flex" align="middle">
                    <Col :xs="24" :sm="24" :md="12" :lg="12">
                        <div class="about-text">
                            <h3>互联造物 - 链接万物，创造未来</h3>
                            <p>
                                安徽勺子同学科技有限公司是一家专注于云计算服务与技术创新的科技企业。
                                公司致力于为企业和个人用户提供安全、稳定、高效的云端解决方案，助力数字化转型。
                            </p>
                            <p>
                                我们秉承"创新、专业、诚信、共赢"的理念，不断探索云计算技术的最新发展，
                                为客户创造更大的价值。
                            </p>
                            <div class="about-stats">
                                <div class="stat-item">
                                    <strong>100+</strong>
                                    <span>服务客户</span>
                                </div>
                                <div class="stat-item">
                                    <strong>5年+</strong>
                                    <span>行业经验</span>
                                </div>
                                <div class="stat-item">
                                    <strong>99.9%</strong>
                                    <span>服务可用性</span>
                                </div>
                            </div>
                        </div>
                    </Col>
                    <Col :xs="24" :sm="24" :md="12" :lg="12">
                        <div class="about-image">
                            <!-- <img src="@/assets/info.png" alt="公司图片" class="img-responsive"> -->
                        </div>
                    </Col>
                </Row>
            </div>
        </section>

        <!-- 核心业务 -->
        <section class="section service-section">
            <div class="container">
                <div class="section-title">
                    <h2>核心业务</h2>
                    <p>OUR SERVICES</p>
                </div>
                <Row :gutter="30">
                    <Col :xs="24" :sm="12" :md="6" :lg="6" v-for="(service, index) in serviceList" :key="index">
                        <div class="service-card">
                            <div class="service-icon">
                                <Icon :type="service.icon" size="48" />
                            </div>
                            <h3>{{ service.title }}</h3>
                            <p>{{ service.description }}</p>
                            <div class="service-hover">
                                <Icon :type="service.icon" size="48" />
                                <h3>{{ service.title }}</h3>
                                <p>{{ service.description }}</p>
                            </div>
                        </div>
                    </Col>
                </Row>
            </div>
        </section>

        <!-- 我们的优势 -->
        <section class="section advantage-section">
            <div class="container">
                <div class="section-title">
                    <h2>我们的优势</h2>
                    <p>OUR ADVANTAGES</p>
                </div>
                <Row :gutter="30">
                    <Col :xs="24" :sm="12" :md="6" :lg="6" v-for="(advantage, index) in advantageList" :key="index">
                        <div class="advantage-card">
                            <div class="advantage-number">{{ String(index + 1).padStart(2, '0') }}</div>
                            <h3>{{ advantage.title }}</h3>
                            <p>{{ advantage.description }}</p>
                        </div>
                    </Col>
                </Row>
            </div>
        </section>

        <!-- 电子合同 -->
        <section class="section product-section" id="electronic-contract">
            <div class="container">
                <div class="section-title">
                    <h2>电子合同</h2>
                    <p>ELECTRONIC CONTRACT</p>
                </div>
                <Row :gutter="60" type="flex" align="middle">
                    <Col :xs="24" :sm="24" :md="12" :lg="12">
                        <div class="product-content">
                            <h3>便捷高效的电子签章服务</h3>
                            <p>
                                我们的电子合同系统提供一站式合同签署、管理、存储服务。
                                支持多种签署方式，合法合规、安全可靠，助力企业数字化转型。
                            </p>
                            <ul class="product-features">
                                <li><Icon type="ios-checkmark-circle" color="#2d8cf0" /> 在线签署，随时随地完成签约</li>
                                <li><Icon type="ios-checkmark-circle" color="#2d8cf0" /> 多种签署方式，满足不同场景需求</li>
                                <li><Icon type="ios-checkmark-circle" color="#2d8cf0" /> 区块链存证，法律效力有保障</li>
                                <li><Icon type="ios-checkmark-circle" color="#2d8cf0" /> 智能合同管理，高效便捷</li>
                            </ul>
                            <Button type="primary" size="large">立即体验</Button>
                        </div>
                    </Col>
                    <Col :xs="24" :sm="24" :md="12" :lg="12">
                        <div class="product-image contract-image">
                            <Icon type="ios-document" size="120" />
                        </div>
                    </Col>
                </Row>
            </div>
        </section>

        <!-- 餐饮系统 -->
        <section class="section product-section-light" id="catering-system">
            <div class="container">
                <div class="section-title">
                    <h2>餐饮系统</h2>
                    <p>CATERING SYSTEM</p>
                </div>
                <Row :gutter="60" type="flex" align="middle">
                    <Col :xs="24" :sm="24" :md="12" :lg="12">
                        <div class="product-image catering-image">
                            <Icon type="ios-restaurant" size="120" />
                        </div>
                    </Col>
                    <Col :xs="24" :sm="24" :md="12" :lg="12">
                        <div class="product-content">
                            <h3>一站式餐饮管理解决方案</h3>
                            <p>
                                我们的餐饮系统集点餐、收银、库存管理、会员营销于一体，
                                操作简单、功能强大，帮助餐饮企业提升运营效率，降低成本。
                            </p>
                            <ul class="product-features">
                                <li><Icon type="ios-checkmark-circle" color="#2d8cf0" /> 多种点餐方式，支持扫码点餐</li>
                                <li><Icon type="ios-checkmark-circle" color="#2d8cf0" /> 智能收银系统，对账轻松</li>
                                <li><Icon type="ios-checkmark-circle" color="#2d8cf0" /> 库存实时管理，避免浪费</li>
                                <li><Icon type="ios-checkmark-circle" color="#2d8cf0" /> 会员营销系统，提升复购</li>
                            </ul>
                            <Button type="primary" size="large">了解更多</Button>
                        </div>
                    </Col>
                </Row>
            </div>
        </section>

        <!-- 合作伙伴/客户展示 -->
        <!-- <section class="section partner-section">
            <div class="container">
                <div class="section-title">
                    <h2>合作伙伴</h2>
                    <p>PARTNERS</p>
                </div>
                <Row :gutter="20" type="flex" align="middle" justify="center">
                    <Col :xs="12" :sm="8" :md="4" :lg="4" v-for="i in 6" :key="i">
                        <div class="partner-logo">
                            <Icon type="logo-windows" size="48" />
                            <span>合作伙伴 {{ i }}</span>
                        </div>
                    </Col>
                </Row>
            </div>
        </section> -->

        <!-- 联系我们 -->
        <section class="section contact-section">
            <div class="container">
                <div class="section-title">
                    <h2>联系我们</h2>
                    <p>CONTACT US</p>
                </div>
                <Row :gutter="60">
                    <Col :xs="24" :sm="24" :md="12" :lg="12">
                        <div class="contact-info">
                            <div class="contact-item">
                                <Icon type="ios-location" size="24" class="contact-icon" />
                                <div>
                                    <h4>地址</h4>
                                    <p>{{ companyInfo.address }}</p>
                                </div>
                            </div>
                            <div class="contact-item">
                                <Icon type="ios-call" size="24" class="contact-icon" />
                                <div>
                                    <h4>电话</h4>
                                    <p>{{ companyInfo.tel }}</p>
                                </div>
                            </div>
                            <div class="contact-item">
                                <Icon type="ios-mail" size="24" class="contact-icon" />
                                <div>
                                    <h4>邮箱</h4>
                                    <p>{{ companyInfo.email }}</p>
                                </div>
                            </div>
                            <div class="contact-item">
                                <Icon type="ios-time" size="24" class="contact-icon" />
                                <div>
                                    <h4>工作时间</h4>
                                    <p>周一至周五 9:00-18:00</p>
                                </div>
                            </div>
                        </div>
                    </Col>
                    <Col :xs="24" :sm="24" :md="12" :lg="12">
                        <div class="contact-qrcode">
                            <!-- <img src="@/assets/WeChat.png" alt="微信公众号" class="qrcode-img"> -->
                            <p>扫码关注我们</p>
                            <p class="qrcode-tip">获取更多资讯</p>
                        </div>
                    </Col>
                </Row>
            </div>
        </section>

        <!-- 底部 -->
        <footer class="footer">
            <div class="container">
                <Row :gutter="40">
                    <Col :xs="24" :sm="24" :md="8" :lg="8">
                        <div class="footer-brand">
                            <img src="@/assets/logo.png" alt="logo" class="footer-logo">
                            <p>{{ companyInfo.name }}</p>
                        </div>
                    </Col>
                    <Col :xs="12" :sm="8" :md="4" :lg="4">
                        <div class="footer-links">
                            <h4>产品服务</h4>
                            <ul>
                                <li><a href="#">云服务器</a></li>
                                <li><a href="#">数据存储</a></li>
                                <li><a href="#">安全服务</a></li>
                                <li><a href="#">技术支持</a></li>
                            </ul>
                        </div>
                    </Col>
                    <Col :xs="12" :sm="8" :md="4" :lg="4">
                        <div class="footer-links">
                            <h4>关于我们</h4>
                            <ul>
                                <li><a href="#">公司介绍</a></li>
                                <li><a href="#">企业文化</a></li>
                                <li><a href="#">发展历程</a></li>
                                <li><a href="#">加入我们</a></li>
                            </ul>
                        </div>
                    </Col>
                    <Col :xs="24" :sm="8" :md="8" :lg="8">
                        <div class="footer-contact">
                            <h4>联系我们</h4>
                            <p><Icon type="ios-call-outline" /> {{ companyInfo.tel }}</p>
                            <p><Icon type="ios-mail-outline" /> {{ companyInfo.email }}</p>
                        </div>
                    </Col>
                </Row>
                <div class="footer-bottom">
                    <p>© {{ currentYear }} {{ companyInfo.name }} 版权所有 | 
                       <a href="#">隐私政策</a> | 
                       <a href="#">服务条款</a>
                    </p>
                </div>
            </div>
        </footer>
        
        <!-- 返回顶部 -->
        <BackTop :bottom="80" :right="30" :duration="1000">
            <div class="back-top">
                <Icon type="ios-arrow-up" size="24" />
            </div>
        </BackTop>
    </div>
</template>

<script>
/**
 * 主布局组件
 * @description 官网首页主布局，包含头部、轮播图、内容区块和底部
 */
import { windowsRise } from '@/utils/dom'; // 引入优化后的工具函数

export default {
    name: 'MainLayout',
    
    data() {
        return {
            // 滚动相关
            isScrolled: false,
            mobileMenuOpen: false,
            currentNav: 0,
            
            // 导航列表
            navList: ['首页', '关于我们', '产品服务', '电子合同', '餐饮系统', '联系我们'],
            
            // 轮播图配置
            bannerSettings: {
                autoplay: true,
                autoplaySpeed: 5000,
                loop: true
            },
            
            // 轮播图列表
            bannerList: [
                {
                    image: require('@/assets/banner1.jpg'),
                    title: '云计算解决方案',
                    description: '安全、稳定、高效的云端服务，助力企业数字化转型',
                    buttonText: '了解更多'
                },
                {
                    image: require('@/assets/banner2.jpg'),
                    title: '专业技术支持',
                    description: '7×24小时专业技术团队，随时响应您的需求',
                    buttonText: '立即咨询'
                },
                {
                    image: require('@/assets/banner3.jpg'),
                    title: '数据安全保障',
                    description: '全方位数据安全防护，保障您的业务安全',
                    buttonText: '安全咨询'
                }
            ],
            
            // 业务列表
            serviceList: [
                {
                    icon: 'ios-cloud',
                    title: '云服务器',
                    description: '提供安全、稳定、高效的云服务器服务，满足企业各种业务需求'
                },
                {
                    icon: 'ios-folder',
                    title: '数据存储',
                    description: '企业级数据存储解决方案，保障数据安全与可靠性'
                },
                {
                    icon: 'ios-shield',
                    title: '安全服务',
                    description: '全方位安全防护，保障云端业务安全稳定运行'
                },
                {
                    icon: 'ios-construct',
                    title: '技术支持',
                    description: '7×24小时专业技术支持，快速响应客户需求'
                },
                {
                    icon: 'ios-document',
                    title: '电子合同',
                    description: '便捷高效的电子签章服务，合法合规、安全可靠'
                },
                {
                    icon: 'ios-restaurant',
                    title: '餐饮系统',
                    description: '一站式餐饮管理解决方案，提升运营效率'
                }
            ],
            
            // 优势列表
            advantageList: [
                {
                    title: '专业团队',
                    description: '拥有经验丰富的技术研发团队，持续创新'
                },
                {
                    title: '稳定可靠',
                    description: '提供7×24小时技术支持，保障业务连续性'
                },
                {
                    title: '灵活定制',
                    description: '根据客户需求提供个性化解决方案'
                },
                {
                    title: '高性价比',
                    description: '让更多企业享受优质云服务'
                }
            ],
            
            // 公司信息
            companyInfo: {
                name: '安徽勺子同学科技有限公司',
                tel: '400-888-8888',
                email: 'info@hulianzaowu.com',
                address: '安徽省'
            }
        };
    },
    
    computed: {
        /**
         * 获取当前年份
         */
        currentYear() {
            return new Date().getFullYear();
        }
    },
    
    mounted() {
        // 初始化
        this.init();
    },
    
    beforeDestroy() {
        // 清理工作
        this.cleanup();
    },
    
    methods: {
        /**
         * 初始化
         */
        init() {
            // 监听窗口滚动
            this.unwatchResize = windowsRise(window, this.handleScroll);
            
            // 初始检查滚动位置
            this.handleScroll();
        },
        
        /**
         * 清理工作
         */
        cleanup() {
            if (this.unwatchResize) {
                this.unwatchResize();
            }
        },
        
        /**
         * 处理滚动事件
         */
        handleScroll() {
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop;
            this.isScrolled = scrollTop > 50;
        },
        
        /**
         * 导航切换
         * @param {Number} index 导航索引
         */
        handleNav(index) {
            this.currentNav = index;
            this.scrollToSection(index);
        },
        
        /**
         * 移动端导航切换
         * @param {Number} index 导航索引
         */
        handleMobileNav(index) {
            this.currentNav = index;
            this.mobileMenuOpen = false;
            this.scrollToSection(index);
        },
        
        /**
         * 滚动到指定区块
         * @param {Number} index 区块索引
         */
        scrollToSection(index) {
            // 根据实际页面结构调整滚动逻辑
            const sections = document.querySelectorAll('.section');
            if (sections[index]) {
                sections[index].scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        },
        
        /**
         * 切换移动端菜单
         */
        toggleMobileMenu() {
            this.mobileMenuOpen = !this.mobileMenuOpen;
        },
        
        /**
         * 返回首页
         */
        goHome() {
            this.currentNav = 0;
            window.scrollTo({ top: 0, behavior: 'smooth' });
        },
        
        /**
         * 轮播图切换回调
         * @param {Number} oldIndex 旧索引
         * @param {Number} newIndex 新索引
         */
        handleBannerChange(oldIndex, newIndex) {
            // 可以在这里添加轮播图切换的逻辑
        }
    }
};
</script>

<style scoped>
/* 容器 */
.container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
}

/* 头部导航 */
.header {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 1000;
    background-color: #fff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    transition: all 0.3s;
}

.header-fixed {
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
}

.header-content {
    max-width: 1200px;
    margin: 0 auto;
    padding: 15px 20px;
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.logo {
    display: flex;
    align-items: center;
    cursor: pointer;
}

.logo-img {
    width: 100%;
    height: 40px;
    margin-right: 10px;
}

.nav-menu {
    display: flex;
    gap: 40px;
}

.nav-item {
    font-size: 16px;
    color: #333;
    cursor: pointer;
    padding: 8px 16px;
    border-radius: 4px;
    transition: all 0.3s;
    position: relative;
}

.nav-item:hover {
    color: #2d8cf0;
}

.nav-item.active {
    color: #2d8cf0;
    font-weight: bold;
}

.nav-item.active::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 30px;
    height: 2px;
    background-color: #2d8cf0;
}

.menu-toggle {
    cursor: pointer;
    padding: 5px;
}

.mobile-menu {
    background-color: #fff;
    padding: 20px;
    border-top: 1px solid #e8eaec;
}

.mobile-nav-item {
    display: block;
    padding: 15px 0;
    color: #333;
    font-size: 16px;
    text-align: center;
    border-bottom: 1px solid #e8eaec;
}

.mobile-nav-item:last-child {
    border-bottom: none;
}

.mobile-nav-item.active {
    color: #2d8cf0;
    font-weight: bold;
}

/* 轮播图 */
.banner-section {
    margin-top: 70px;
}

.banner-item {
    height: 500px;
    background-size: cover;
    background-position: center;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
}

.banner-content {
    text-align: center;
    color: #fff;
    max-width: 800px;
    padding: 0 20px;
}

.banner-title {
    font-size: 48px;
    font-weight: bold;
    margin-bottom: 20px;
    animation: fadeIn 1s ease-out;
}

.banner-desc {
    font-size: 20px;
    margin-bottom: 30px;
    opacity: 0.9;
    animation: fadeIn 1s ease-out 0.2s both;
}

.banner-btn {
    animation: fadeIn 1s ease-out 0.4s both;
}

/* 通用区块 */
.section {
    padding: 80px 0;
    background-color: #fff;
}

.about-section {
    background-color: #f8f9fa;
}

.service-section {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: #fff;
}

.advantage-section {
    background-color: #f8f9fa;
}

/* 产品区块 */
.product-section {
    background-color: #fff;
}

.product-section-light {
    background-color: #f8f9fa;
}

.product-content h3 {
    font-size: 28px;
    margin-bottom: 20px;
    color: #333;
}

.product-content p {
    font-size: 16px;
    line-height: 1.8;
    margin-bottom: 25px;
    color: #666;
}

.product-features {
    list-style: none;
    margin-bottom: 30px;
}

.product-features li {
    font-size: 15px;
    color: #555;
    margin-bottom: 12px;
    display: flex;
    align-items: center;
    gap: 10px;
}

.product-image {
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 12px;
    padding: 60px;
    min-height: 300px;
}

.contract-image {
    color: #fff;
}

.catering-image {
    background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
    color: #fff;
}

.product-image i {
    opacity: 0.9;
}

.partner-section {
    background-color: #fff;
}

.contact-section {
    background-color: #f8f9fa;
}

/* 区块标题 */
.section-title {
    text-align: center;
    margin-bottom: 60px;
}

.section-title h2 {
    font-size: 36px;
    color: #333;
    margin-bottom: 10px;
}

.section-title p {
    font-size: 14px;
    color: #999;
    letter-spacing: 4px;
}

.service-section .section-title h2,
.service-section .section-title p {
    color: #fff;
}

/* 关于我们 */
.about-text h3 {
    font-size: 28px;
    margin-bottom: 20px;
    color: #333;
}

.about-text p {
    font-size: 16px;
    line-height: 1.8;
    margin-bottom: 15px;
    color: #666;
}

.about-stats {
    display: flex;
    gap: 40px;
    margin-top: 30px;
}

.stat-item {
    text-align: center;
}

.stat-item strong {
    display: block;
    font-size: 36px;
    color: #2d8cf0;
    margin-bottom: 5px;
}

.stat-item span {
    font-size: 14px;
    color: #666;
}

.about-image img {
    width: 100%;
    border-radius: 8px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
}

/* 服务卡片 */
.service-card {
    position: relative;
    text-align: center;
    padding: 40px 20px;
    background-color: rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    margin-bottom: 20px;
    transition: all 0.3s;
    overflow: hidden;
}

.service-card:hover {
    transform: translateY(-10px);
    background-color: rgba(255, 255, 255, 0.2);
}

.service-icon {
    margin-bottom: 20px;
}

.service-card h3 {
    font-size: 20px;
    margin-bottom: 15px;
}

.service-card p {
    font-size: 14px;
    line-height: 1.6;
    opacity: 0.9;
}

.service-hover {
    display: none;
}

/* 优势卡片 */
.advantage-card {
    text-align: center;
    padding: 40px 20px;
    background-color: #fff;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    margin-bottom: 20px;
    transition: all 0.3s;
}

.advantage-card:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

.advantage-number {
    font-size: 48px;
    font-weight: bold;
    color: #2d8cf0;
    margin-bottom: 20px;
}

.advantage-card h3 {
    font-size: 20px;
    color: #333;
    margin-bottom: 15px;
}

.advantage-card p {
    font-size: 14px;
    color: #666;
    line-height: 1.6;
}

/* 合作伙伴 */
.partner-logo {
    text-align: center;
    padding: 30px 20px;
    border: 1px solid #e8eaec;
    border-radius: 8px;
    transition: all 0.3s;
}

.partner-logo:hover {
    border-color: #2d8cf0;
    box-shadow: 0 4px 12px rgba(45, 140, 240, 0.2);
}

.partner-logo span {
    display: block;
    margin-top: 10px;
    font-size: 14px;
    color: #666;
}

/* 联系方式 */
.contact-info {
    padding: 20px;
}

.contact-item {
    display: flex;
    align-items: flex-start;
    gap: 20px;
    margin-bottom: 30px;
}

.contact-icon {
    color: #2d8cf0;
    flex-shrink: 0;
}

.contact-item h4 {
    font-size: 16px;
    color: #333;
    margin-bottom: 5px;
}

.contact-item p {
    font-size: 14px;
    color: #666;
}

.contact-qrcode {
    text-align: center;
    padding: 40px 20px;
}

.qrcode-img {
    width: 200px;
    height: 200px;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    margin-bottom: 20px;
}

.qrcode-tip {
    font-size: 14px;
    color: #999;
    margin-top: 10px;
}

/* 底部 */
.footer {
    background-color: #333;
    color: #fff;
    padding: 60px 0 30px;
}

.footer-brand {
    margin-bottom: 20px;
}

.footer-logo {
    width: 80px;
    margin-bottom: 10px;
}

.footer-brand p {
    font-size: 14px;
    color: #999;
}

.footer-links h4 {
    font-size: 16px;
    margin-bottom: 20px;
    color: #fff;
}

.footer-links ul {
    list-style: none;
}

.footer-links li {
    margin-bottom: 10px;
}

.footer-links a {
    color: #999;
    font-size: 14px;
    transition: color 0.3s;
}

.footer-links a:hover {
    color: #fff;
}

.footer-contact h4 {
    font-size: 16px;
    margin-bottom: 20px;
    color: #fff;
}

.footer-contact p {
    font-size: 14px;
    color: #999;
    margin-bottom: 10px;
}

.footer-bottom {
    margin-top: 40px;
    padding-top: 20px;
    border-top: 1px solid #555;
    text-align: center;
}

.footer-bottom p {
    font-size: 14px;
    color: #999;
}

.footer-bottom a {
    color: #999;
    margin: 0 10px;
}

.footer-bottom a:hover {
    color: #fff;
}

/* 返回顶部 */
.back-top {
    width: 48px;
    height: 48px;
    background-color: rgba(45, 140, 240, 0.8);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    transition: all 0.3s;
}

.back-top:hover {
    background-color: #2d8cf0;
}

/* 响应式设计 */
@media (max-width: 768px) {
    .header-content {
        padding: 15px;
    }
    
    .banner-section {
        margin-top: 60px;
    }
    
    .banner-item {
        height: 400px;
    }
    
    .banner-title {
        font-size: 32px;
    }
    
    .banner-desc {
        font-size: 16px;
    }
    
    .section {
        padding: 60px 0;
    }
    
    .section-title h2 {
        font-size: 28px;
    }
    
    .about-stats {
        flex-direction: column;
        gap: 20px;
    }
    
    .footer-bottom {
        text-align: left;
    }
    
    .footer-bottom a {
        display: block;
        margin: 10px 0;
    }
}
</style>
